import React, { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from "react";
import { registerSeek, unregisterSeek } from "../../../_visor/utils/recorderSeekRegistry";
import { isRecorderMode } from "../../../_visor/utils/recorderMode";
import { credentialsFor } from "../../../common/utils";
import { sceneTimeBus } from "../../../common/scene_clock.es6";
import WORKER_SOURCE from "./animatedImageDecode.worker.js?raw";

/* eslint-disable react/prop-types */

// Recorder-mode renderer for animated images (WebP / animated PNG / GIF).
// The browser plays animated WebPs on wall-clock — fine for live playback,
// but the seek-frame loop captures frames much faster than wall-clock, so
// an 8 s WebP finishes in ~1 s of captured video and stalls on its last
// frame for the rest. Decode the file via WebCodecs ImageDecoder, hold each
// frame's bitmap, and pick the right one for the current visorFrameTime.
//
// Static formats (single-frame PNG/JPG/SVG/static WebP) decode to a single
// frame with no duration; we just paint that once on mount.

// WebP ANIM loop count: 0 = infinite, N = play N times. Null if not an animated WebP.
export function webpLoopCount(buf) {
  const dv = new DataView(buf);
  if (dv.byteLength < 21) return null;
  if (dv.getUint32(0, false) !== 0x52494646 /* RIFF */) return null;
  if (dv.getUint32(8, false) !== 0x57454250 /* WEBP */) return null;
  let off = 12;
  while (off + 8 <= dv.byteLength) {
    const fourcc = dv.getUint32(off, false);
    const size = dv.getUint32(off + 4, true);
    if (fourcc === 0x414e494d /* ANIM */) return dv.getUint16(off + 8 + 4, true);
    if (fourcc === 0x414e4d46 /* ANMF */) return null; // ANIM must precede ANMF
    off += 8 + size + (size % 2); // chunks are even-padded
  }
  return null;
}

// Hard ceiling so a malformed file can't spin forever.
const MAX_FRAMES = 4096;

// The frames of an image file, one by one: `onMeta({ repetitionCount,
// frameCount })`, then `onFrame(videoFrame, complete)` per frame. `done`
// settles at the end of the file and rejects on a decode error (a RangeError
// "frame index out of range" is the end, not an error). After `cancel()`
// nothing more is delivered.
function decodeHere(buf, type, { onMeta, onFrame }) {
  let cancelled = false;
  const done = (async () => {
    const decoder = new window.ImageDecoder({ data: buf, type });
    try {
      await decoder.completed;
      // Chrome's ImageDecoder can under-report frameCount for animated
      // WebPs: decode({frameIndex}) until it says the index is past the end.
      const track = decoder.tracks.selectedTrack;
      onMeta({ repetitionCount: track?.repetitionCount, frameCount: track?.frameCount });
      for (let i = 0; i < MAX_FRAMES; i++) {
        if (cancelled) return;
        let result;
        try {
          result = await decoder.decode({ frameIndex: i });
        } catch (e) {
          if (e && e.name === "RangeError") break;
          throw e;
        }
        if (cancelled) {
          result.image.close(); // came in after the cancel
          return;
        }
        onFrame(result.image, result.complete !== false);
        if (result.complete === false) break; // partial frame: keep it, stop
      }
    } finally {
      decoder.close?.();
    }
  })();
  return { done, cancel: () => (cancelled = true) };
}

// The same, on a worker thread: decoding a long file no longer waits for a
// main thread that is busy playing the scene. Frames are transferred back.
// The worker starts from a Blob URL of its source, not from a URL of its own:
// the standalone Visor is served from a CDN, and a worker script must come
// from the page's origin.
function decodeOnWorker(buf, type, { onMeta, onFrame }) {
  const src = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: "text/javascript" }));
  let worker;
  try {
    worker = new Worker(src);
  } catch (e) {
    URL.revokeObjectURL(src);
    throw e;
  }
  let cancelled = false;
  let frames = 0;
  const stop = () => {
    worker.terminate();
    URL.revokeObjectURL(src);
  };
  const done = new Promise((resolve, reject) => {
    worker.onmessage = (e) => {
      const m = e.data;
      if (cancelled) {
        if (m.frame) m.frame.close(); // posted before the cancel reached it
        return;
      }
      if (m.kind === "meta") onMeta(m);
      else if (m.kind === "frame") {
        frames++;
        onFrame(m.frame, m.complete);
      } else if (m.kind === "end") {
        stop();
        resolve();
      } else if (m.kind === "error") {
        stop();
        reject(Object.assign(new Error(m.message), { framesBefore: frames }));
      }
    };
    worker.onerror = (ev) => {
      if (ev && ev.preventDefault) ev.preventDefault();
      stop();
      reject(
        Object.assign(new Error("image decode worker failed"), {
          framesBefore: frames,
          worker: true,
        })
      );
    };
  });
  worker.postMessage({ buf, type }, [buf]);
  return {
    done,
    cancel: () => {
      cancelled = true;
      worker.postMessage({ cancel: true });
      stop();
    },
  };
}

export default function RecorderCanvasImage({
  url,
  visorFrameTime,
  boxStartFrame,
  fps = 30,
  show = true,
  // The scene clock runs. While it does not, a frame decoded meanwhile is
  // drawn only for a seek (a new target), never just because it arrived.
  playing = true,
  // Changes with every explicit playhead move (the Visor's sceneSeek id). It
  // tells a seek while paused from the update that settles a pause; without
  // it, a move of more than a frame counts as a seek.
  seekId,
  // Visor live playback: follow this scene's committed frame, not the prop.
  frameSceneId,
  onReady,
  onDecodeFailed,
  style,
  imgClassName = "basicImageClass",
  imgStyle = {},
}) {
  const canvasRef = useRef(null);
  const framesRef = useRef([]); // [{frame: VideoFrame, durationUs: int}]
  const totalDurRef = useRef(0); // µs
  const playsRef = useRef(Infinity); // total play-throughs; Infinity = loop forever
  const errorRef = useRef(null);
  const lastLoggedFrameRef = useRef(-1);
  // Live playback draws as soon as the first frame is decoded; the rest keep
  // arriving (completeRef). A time not decoded yet shows the last decoded
  // frame (heldRef) and is drawn again when its frame comes in.
  const completeRef = useRef(false);
  const targetRef = useRef(0);
  const heldRef = useRef(false);
  // Recorder mode: every frame is decoded. Live: at least the first one.
  const [ready, setReady] = useState(false);
  const [decodeFailed, setDecodeFailed] = useState(false);
  const recorderMode = isRecorderMode();
  // Read by the decode loop, which outlives renders: a hidden scene is not drawn.
  const showRef = useRef(show);
  showRef.current = show;
  const playingRef = useRef(playing);
  playingRef.current = playing;
  // A target set while paused (a seek) still waits for its frame.
  const seekPendingRef = useRef(false);
  const paintedOnceRef = useRef(false);
  const lastSeekIdRef = useRef(seekId);
  const paintedShowRef = useRef(false);
  const decodedRef = useRef(null); // { promise, resolve } — settles once decode is committed
  const liveBus = !recorderMode && frameSceneId != null;
  const busFrame = () =>
    liveBus && sceneTimeBus.frameSceneId === frameSceneId ? sceneTimeBus.frame : null;
  // Another scene's frame leaves the snapshot as it was.
  const ownFrameRef = useRef(null);
  const committedFrame = useSyncExternalStore(sceneTimeBus.subscribeFrame, () => {
    const f = busFrame();
    if (f !== null) ownFrameRef.current = f;
    return ownFrameRef.current;
  });
  const timingRef = useRef({ boxStartFrame, fps });
  timingRef.current = { boxStartFrame, fps };

  // Recorder mode: join the seek-frame loop (runAllSeeks awaits every handler)
  // at mount, before decoding. A box mounting mid-scene otherwise got captured
  // as the wall-clock <img> fallback while its frames decoded — the animation
  // ran ahead, then snapped back once the canvas took over. Layout effect so
  // the handler is registered before the host's seek reaches runAllSeeks.
  useLayoutEffect(() => {
    if (!recorderMode) return undefined;
    let resolve;
    const promise = new Promise((r) => {
      resolve = r;
    });
    decodedRef.current = { promise, resolve };
    const seekId = `rci-${url}-${Math.random().toString(36).slice(2)}`;
    registerSeek(seekId, async (sceneSec) => {
      await Promise.race([promise, new Promise((r) => setTimeout(r, 15000))]);
      const { boxStartFrame: start, fps: rate } = timingRef.current;
      paintAtTime(Math.max(0, sceneSec - (start || 0) / rate));
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    });
    return () => {
      unregisterSeek(seekId);
      resolve();
    };
  }, [url]);

  // Settle only after the ready/failed state has committed, so the canvas
  // (not the loading <img>) is what the host captures.
  useEffect(() => {
    if ((ready || decodeFailed) && decodedRef.current) decodedRef.current.resolve();
  }, [ready, decodeFailed]);

  useEffect(() => {
    let cancelled = false;
    let job = null;
    const decodedFrames = [];
    framesRef.current = decodedFrames;
    totalDurRef.current = 0;
    completeRef.current = false;

    // Recorder: a decode stuck past its seek handler's cap falls back to the
    // wall-clock <img> rather than stalling capture. Live playback has no such
    // cap: the <img> shows until the first frame is decoded, however long that
    // takes, and a slow decode never becomes a permanent fallback.
    const readinessTimeout = recorderMode
      ? setTimeout(() => {
          if (cancelled) return;
          // eslint-disable-next-line no-console
          console.warn("[RecorderCanvasImage] decode timed out; falling back to <img>:", url);
          setDecodeFailed(true);
          if (typeof onDecodeFailed === "function") onDecodeFailed();
          if (typeof onReady === "function") onReady();
        }, 15000)
      : null;

    (async () => {
      try {
        if (typeof window.ImageDecoder === "undefined") {
          throw new Error("ImageDecoder API not supported");
        }
        // The preloader already downloaded these bytes; fetching again is a
        // second full transfer of the same file.
        const preloaded = window.__visorImageBlobData && window.__visorImageBlobData[url];
        let buf;
        if (preloaded) {
          buf = await preloaded.arrayBuffer();
        } else {
          const resp = await fetch(url, {
            mode: "cors",
            credentials: "omit",
            ...(recorderMode ? {} : credentialsFor(url)),
          });
          if (!resp.ok) throw new Error(`fetch failed: ${resp.status}`);
          buf = await resp.arrayBuffer();
        }
        // Removed (or a new url) while the bytes were on their way.
        if (cancelled) return;
        const fileLoops = webpLoopCount(buf);
        // ImageDecoder needs a MIME type. Browser auto-sniffs from URL extension
        // for the common cases — we pass a hint when we can.
        const lower = (url || "").toLowerCase();
        let type = "image/webp";
        if (lower.includes(".gif")) type = "image/gif";
        else if (lower.includes(".png")) type = "image/png";
        else if (lower.includes(".jpg") || lower.includes(".jpeg")) type = "image/jpeg";
        else if (lower.includes(".avif")) type = "image/avif";

        let reportedCount;
        let totalDurUs = 0;
        const handlers = {
          onMeta: ({ repetitionCount, frameCount }) => {
            reportedCount = frameCount;
            // Normalize to total play-throughs. The WebP ANIM loop count is
            // authoritative when present (Blink passes it through as
            // repetitionCount verbatim, which does NOT match the WebCodecs
            // "repeats after first play" meaning). Other formats fall back to
            // the track value, floored at one play so a "play once" file
            // never wraps.
            playsRef.current =
              fileLoops !== null
                ? fileLoops === 0
                  ? Infinity
                  : fileLoops
                : Number.isFinite(repetitionCount)
                  ? Math.max(1, repetitionCount)
                  : Infinity;
          },
          onFrame: (img, complete) => {
            if (cancelled) {
              img.close();
              return;
            }
            const durUs = img.duration ? Math.round(img.duration) : 0;
            decodedFrames.push({ frame: img, durationUs: durUs });
            totalDurUs += durUs;
            totalDurRef.current = totalDurUs;
            if (!recorderMode) {
              if (decodedFrames.length === 1) setReady(true);
              else showTargetIfDecoded();
            }
          },
        };
        // Live playback decodes on a worker; the recorder (and a browser
        // without one) here.
        const runHere = () => (job = decodeHere(buf, type, handlers));
        if (!recorderMode && typeof Worker !== "undefined") {
          try {
            job = decodeOnWorker(buf.slice(0), type, handlers);
          } catch (_) {
            runHere();
          }
        } else runHere();
        try {
          await job.done;
        } catch (e) {
          // The worker itself could not run here: decode on this thread.
          if (!(e && e.worker && e.framesBefore === 0) || cancelled) throw e;
          runHere();
          await job.done;
        }
        if (cancelled) return;
        // Not even one frame: nothing to draw on the canvas.
        if (!decodedFrames.length) throw new Error("no frame decoded");
        totalDurRef.current = totalDurUs;
        completeRef.current = true;
        clearTimeout(readinessTimeout);
        setReady(true);
        // Now the loop length is known: a held time may wrap to another frame.
        if (!recorderMode) showTargetIfDecoded();
        // eslint-disable-next-line no-console
        console.debug(
          `[RecorderCanvasImage] decoded ${decodedFrames.length} frame(s) (track.frameCount=${reportedCount}, fileLoops=${fileLoops}, plays=${playsRef.current}) totalDur=${(totalDurUs / 1e6).toFixed(3)}s url=...${(url || "").slice(-50)}`
        );
        if (typeof onReady === "function") onReady();
        // Don't force frame 0 here — the [visorFrameTime, ready] effect below
        // fires as soon as `ready` flips true and paints whatever second the
        // timeline is actually sitting on. Painting frame 0 first just to be
        // overwritten a moment later showed the wrong frame if that repaint
        // was ever delayed a tick.
      } catch (err) {
        errorRef.current = err;
        // eslint-disable-next-line no-console
        console.warn("[RecorderCanvasImage] decode failed; falling back to <img>:", err);
        if (cancelled) return;
        clearTimeout(readinessTimeout);
        setDecodeFailed(true);
        if (typeof onDecodeFailed === "function") onDecodeFailed();
        // Still signal ready so the recorder readiness gate can proceed —
        // we'll fall back to a plain <img> render path.
        if (typeof onReady === "function") onReady();
      }
    })();

    return () => {
      cancelled = true;
      if (job) job.cancel();
      clearTimeout(readinessTimeout);
      // VideoFrames hold GPU memory; release on unmount.
      framesRef.current.forEach((f) => {
        try {
          f.frame.close();
        } catch (_) {
          /* ignore */
        }
      });
      framesRef.current = [];
    };
  }, [url]);

  // A frame arrived while the scene time is not decoded yet (held). Draw the
  // target only once its own frame is there — not every frame on the way —
  // and while paused only for a seek: a plain pause keeps its picture.
  const showTargetIfDecoded = () => {
    if (!heldRef.current || !showRef.current) return;
    const decoded =
      completeRef.current || Math.round(targetRef.current * 1e6) < totalDurRef.current;
    if (!decoded) return;
    if (!playingRef.current && !seekPendingRef.current) return;
    seekPendingRef.current = false;
    paintAtTime(targetRef.current);
  };

  const paintAtTime = (t) => {
    const frames = framesRef.current;
    // Integer microseconds: 24fps frame edges land exactly on 30fps capture
    // times every 0.5 s, and summing float seconds put some of those edges a
    // hair late — the previous frame repeated there, a hitch twice a second.
    const totalDur = totalDurRef.current;
    const tUs = Math.round(t * 1e6);
    targetRef.current = t;
    const canvas = canvasRef.current;
    if (!canvas || !frames.length) return;
    let frame = frames[0];
    let frameIndex = 0;
    const wasHeld = heldRef.current;
    heldRef.current = !completeRef.current && tUs >= totalDur;
    if (heldRef.current !== wasHeld) {
      // eslint-disable-next-line no-console
      console.debug(
        `[RecorderCanvasImage] ${heldRef.current ? "held" : "caught up"} @ t=${t.toFixed(3)}s url=...${(url || "").slice(-40)}`
      );
    }
    if (heldRef.current) {
      // Not decoded yet: keep the newest frame there is until it is.
      frame = frames[frames.length - 1];
      frameIndex = frames.length - 1;
    } else if (frames.length > 1 && totalDur > 0) {
      const plays = playsRef.current;
      if (Number.isFinite(plays) && tUs >= plays * totalDur) {
        // File plays a finite number of times and we're past the end —
        // hold the last frame, matching browser playback.
        frame = frames[frames.length - 1];
        frameIndex = frames.length - 1;
      } else {
        // Loop the WebP — only when the file itself says to.
        const wrapped = ((tUs % totalDur) + totalDur) % totalDur;
        let acc = 0;
        for (let i = 0; i < frames.length; i++) {
          acc += frames[i].durationUs;
          if (wrapped < acc) {
            frame = frames[i];
            frameIndex = i;
            break;
          }
        }
      }
    }
    if (frameIndex !== lastLoggedFrameRef.current) {
      lastLoggedFrameRef.current = frameIndex;
      // eslint-disable-next-line no-console
      console.debug(
        `[RecorderCanvasImage] frame ${frameIndex}/${frames.length - 1} @ t=${t.toFixed(3)}s url=...${(url || "").slice(-40)}`
      );
    }
    const vf = frame.frame;
    const w = vf.displayWidth || vf.codedWidth;
    const h = vf.displayHeight || vf.codedHeight;
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
    }
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(vf, 0, 0, w, h);
  };

  useEffect(() => {
    if (!ready || decodeFailed) return;
    // Every scene stays mounted and gets the frame tick, so a hidden one would
    // keep drawing full frames it never shows. `show` in the deps repaints on
    // re-entry.
    if (!show && !recorderMode) return;
    // Read now: a seek's id can render before its frame is committed.
    const frameNow = busFrame() ?? visorFrameTime;
    const t = Math.max(0, ((frameNow || 0) - (boxStartFrame || 0)) / fps);
    const shownNow = show && !paintedShowRef.current;
    paintedShowRef.current = show;
    const seeked =
      seekId !== undefined
        ? seekId !== lastSeekIdRef.current
        : Math.abs(t - targetRef.current) > 1.5 / fps;
    lastSeekIdRef.current = seekId;
    if (!playing && !recorderMode && !shownNow && paintedOnceRef.current) {
      // Paused: only a seek changes the picture (its frame shown once
      // decoded); the update that settles the stopped clock does not.
      if (!seeked) return;
      seekPendingRef.current = true;
    } else {
      seekPendingRef.current = !playing;
    }
    paintedOnceRef.current = true;
    paintAtTime(t);
    // Live: only the committed frame moves it (the prop would paint twice).
  }, [
    liveBus ? null : visorFrameTime,
    committedFrame,
    boxStartFrame,
    fps,
    ready,
    decodeFailed,
    show,
    playing,
    seekId,
  ]);

  // Preloader's bytes when resident, so the fallback costs no transfer.
  const fallbackSrc =
    (typeof window.__visorImageObjectUrl === "function" && window.__visorImageObjectUrl(url)) ||
    url;

  if (decodeFailed) {
    // Browser will play wall-clock but at least the user sees something.
    return (
      <img
        className={imgClassName}
        src={fallbackSrc}
        style={{ width: "100%", height: "auto", ...imgStyle }}
      />
    );
  }

  // Wrap canvas to match <img>-like layout the rest of the visor expects.
  return (
    <>
      {/* While decoding (every fresh mount re-decodes all frames — no cross-
          mount cache), show the plain wall-clock <img> instead of a blank
          canvas. Without this a box that re-enters the timeline's active
          range (e.g. after "restart to start") flashes blank/white until
          the full frame-by-frame decode finishes. */}
      {!ready && (
        <img
          className={imgClassName}
          src={fallbackSrc}
          style={{ width: "100%", height: "auto", ...imgStyle }}
        />
      )}
      <canvas
        ref={canvasRef}
        className={imgClassName}
        style={{
          width: "100%",
          height: "auto",
          display: ready ? "block" : "none",
          ...style,
          ...imgStyle,
        }}
      />
    </>
  );
}

/* eslint-enable react/prop-types */
